import React, { useState, useEffect, useRef, useContext } from 'react';
import { scroller } from 'react-scroll';
import { useLazyQuery, gql } from '@apollo/client';

import { Day } from 'components';
import { DebugContext } from 'utils/DebugContext';
import { TRACK, recordEvent } from 'utils/Track';
import { throttle } from 'utils/Time';

import appIcon from 'assets/images/ooo-app-icon.png';
import { ReactComponent as LinkArrowSvg } from 'assets/svg/arrow-diagonal.svg';

const TRIP = gql`
  query($tripId: ID!) {
    getTrip(tripId: $tripId) {
      id
      description
    }
  }
`;

const POLLING_INTERVAL = 2000;

// Parse OpenAI's HTML string for morning/afternoon/evening descriptions of all days, split by {{Day X}} tags
const generateDayDescriptions = (descriptionString) => {
  const cleanDescription = descriptionString?.replace(/[\u0000-\u001F\u007F-\u009F\u200B]/g, ''); // Remove control and invisible chars
  const openingBracket = cleanDescription.includes('{{') ? '{{' : '{';
  const closingBracketsToRemove = ['}}', '}'];
  const regexBracketsToRemove = new RegExp(closingBracketsToRemove.join('|'), 'g');

  const regexDayInTag = /(<[^>]*>)Day\s\d+(<\/[^>]*>)/g;

  const modifiedDescription = cleanDescription?.replace(regexDayInTag, (match, p1, p2) => {
    return `{{${match.slice(p1.length, -p2.length)}}}`;
  });

  const daysArr = modifiedDescription
    .split(`${openingBracket}Day `)
    .slice(1)
    .map(day => day?.replace(regexBracketsToRemove, ''))
    .map(day => {
      const substringIndexStart = day.indexOf('<p><strong>');
      const substringIndexEnd = day.lastIndexOf('</p>') + '</p>'.length;
      return day.substring(substringIndexStart, substringIndexEnd);
    });
  return daysArr;
};

const ScheduleSection = React.forwardRef(({ trip, onHideSchedule, onSetDocumentTitle, shareUrl }, ref) => {
  const {
    description,
    itinerary = [],
    photos = [],
    regions = []
  } = trip;
  const { isDebug } = useContext(DebugContext);

  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [isAppLinkVisible, setIsAppLinkVisible] = useState(false);
  const [pollInterval, setPollInterval] = useState(0);
  const [descriptions, setDescriptions] = useState([]);
  const eventContext = {
    section: TRACK.SECTIONS.SCHEDULE,
    tripId: trip?.id,
    tripName: trip?.name,
    regionName: regions[0]?.name,
  };

  const [doGetDescriptions, { data: descriptionsData, error: descriptionsError }] = useLazyQuery(TRIP, {
    variables: { tripId: trip?.id },
    pollInterval,
    onCompleted: (fetchedData) => {
      isDebug && console.log('Data polled:', fetchedData);
    },
    onError: (error) => {
      isDebug && console.log('Error polling:', error);
    }
  });

  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const totalDays = itinerary?.length;

  const heroImageUrl = regions?.[0]?.heroImageUrl || photos?.[0];

  const startPolling = () => {
    setPollInterval(POLLING_INTERVAL);
  };

  const scrollToDay = (dayNumber) => {
    recordEvent(TRACK.EVENTS.SCROLL_TO_DAY, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
      dayNumber
    });
    scroller.scrollTo(`day-${dayNumber}`, {
      duration: 800,
      delay: 0,
      smooth: 'easeInOutQuart',
      containerId: 'content-right',
      offset: -33
    });
  };

  const throttledSetSelectedDayIndex = throttle(setSelectedDayIndex, 100);

  const handleScroll = () => {
    const container = containerRef.current; // The schedule container that the user is scrolling
    const scrollTop = container.scrollTop; // How far the user has scrolled
    const mobileLinkScrollThreshold = 50; // How far the user has to scroll before the app link appears
    const scrollFactor = 0.5; // How much the hero image should scroll relative to the scroll of the container

    const newHeroOffset = scrollTop * scrollFactor;

    requestAnimationFrame(() => {
      heroRef.current.style.backgroundPosition = `50% ${newHeroOffset}px`;
    });

    const isShouldShowAppLink = scrollTop > mobileLinkScrollThreshold && !isAppLinkVisible;
    setIsAppLinkVisible(isShouldShowAppLink);

    if (scrollTop > mobileLinkScrollThreshold && !isAppLinkVisible) {
      setIsAppLinkVisible(true);
    } else {
      setIsAppLinkVisible(false);
    }

    const dayElements = container.querySelectorAll('.day-description');
    let currentDayIndex = selectedDayIndex;
    // Loop through all the day elements and find the first one that is below the top third of the window
    for (let i = 0; i < dayElements.length; i++) {
      const dayRect = dayElements[i].getBoundingClientRect();
      if (dayRect.bottom > window.innerHeight / 3) {
        // If the bottom of the day element is below the top third of the window, then we've found the current day
        currentDayIndex = i;
        break;
      }
    }
    throttledSetSelectedDayIndex(currentDayIndex);
  };

  const handleSelectDayToScrollTo = (dayNumber) => {
    recordEvent(TRACK.EVENTS.SELECT_DAY_TO_SCROLL_TO, {
      ...eventContext,
      action: TRACK.ACTIONS.CLICK,
      tripId: trip?.id,
      totalDays,
      regionName: regions[0]?.name,
      dayNumber
    });
    scrollToDay(dayNumber);
  };

  useEffect(() => {
    if (trip?.id && description === null) {
      isDebug && console.log(`Day descriptions polling started (every ${POLLING_INTERVAL}ms)...`);
      setDescriptions([]);
      startPolling();
      doGetDescriptions();
    } else if (description !== null) {
      setDescriptions(generateDayDescriptions(description));
    }
  }, [trip]);

  useEffect(() => {
    if (descriptionsData?.getTrip?.description) {
      isDebug && console.log('%c Polling stopped and descriptions fetched', 'background: #222; color: #1abc9c');
      setPollInterval(0); // Stop polling once the description is fetched
      setDescriptions(generateDayDescriptions(descriptionsData?.getTrip?.description));
    }
  }, [descriptionsData]);

  useEffect(() => {
    const container = containerRef.current;
    container.addEventListener('scroll', handleScroll);
    onSetDocumentTitle({ trip });
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section id='content-right' ref={el => { ref.current = el; containerRef.current = el; }}>
      <div className='content-right-inner'>
        <div ref={heroRef} className='hero' style={{ backgroundImage: `url(${heroImageUrl})` }}>
          <button className='create-another-trip' onClick={onHideSchedule}></button>
          <div className='hero-gradient'></div>
          <div className='hero-title-content'>
            <div><span>{/* Your Custom Itinerary / Curated List */}</span></div>
            <h1>Your {totalDays}-Day {regions[0]?.name || ''} Itinerary</h1>
          </div>
        </div>{/* .hero */}
        <div className='schedule'>
          {totalDays > 1
            ? <div className='day-picker'>
              <div className='day-picker-buttons-container'>
                <div className='day-picker-buttons'>
                  {[...Array(totalDays)]?.map((_, index) => (
                    <button
                      key={index}
                      className={index === selectedDayIndex ? 'selected' : ''}
                      onClick={() => handleSelectDayToScrollTo(index + 1)}
                    >
                      Day {index + 1}
                    </button>
                  ))}
                </div>{/* .day-picker-buttons */}
              </div>{/* .day-picker-buttons-container */}
            </div>
            : null
          }
          <div className='itinerary-container'>
            {itinerary?.map((day, index) => (
              <Day
                key={index}
                day={day}
                isDescriptionLoading={!descriptionsData?.getTrip?.description && description === null && !descriptionsError}
                description={descriptions[index]}
                isOnlyDay={totalDays === 1}
                eventContext={eventContext}
                fullDescriptionsString={descriptionsData?.getTrip?.description || description}
              />
            ))}
          </div>{/* .itinerary-container */}
        </div>{/* .schedule */}
        <div className={`mobile-app-link-container ${isAppLinkVisible ? 'mobile-app-link-container-visible' : ''}`}>
          <a href={shareUrl} className='mobile-app-link' target='_blank'>
            <img src={appIcon} alt='Out Of Office App Icon' />
            <div className='app-message-container'>
              <span>Want to save or edit this trip?</span>
              <span>Download the free OOO app</span>
            </div>
            <div className='svg-arrow-container'>
              <LinkArrowSvg width={15} height={16} />
            </div>
          </a>
        </div>{/* .mobile-app-link-container */}
      </div>{/* .content-right-inner */}
    </section>
  )
});

export default ScheduleSection;
